<template>
  <div>
    <div v-for="def in sortedDefs" :key="def.id" class="mb-3">
      <!-- Numero. Kumulatiivisella attribuutilla (def.cumulative ja kertymäpohja annettu) rinnalla
           muokattava Yhteensä-kenttä: kumpaan tahansa kirjoitettu arvo päivittää toisen. -->
      <v-row v-if="def.valueType === 'NUMBER'" dense>
        <v-col cols="12" :sm="isCumulative(def) ? 6 : 12">
          <v-text-field
            :label="labelWithUnit(def)"
            :value="dayDisplay(def)"
            :disabled="disabled"
            :prepend-icon="def.icon"
            type="number"
            outlined
            dense
            hide-details
            @input="v => onDayInput(def, v)"
            @change="v => setNumber(def, v)"
          />
        </v-col>
        <v-col v-if="isCumulative(def)" cols="12" sm="6">
          <v-text-field
            :label="labelTotal(def)"
            :value="totalDisplay(def)"
            :placeholder="totalPlaceholder(def)"
            :disabled="disabled"
            :prepend-icon="def.icon"
            :error-messages="totalError(def)"
            type="number"
            outlined
            dense
            persistent-placeholder
            hide-details="auto"
            @input="v => onTotalInput(def, v)"
            @change="v => setTotal(def, v)"
          />
        </v-col>
      </v-row>
      <!-- Teksti -->
      <v-text-field
        v-else-if="def.valueType === 'TEXT'"
        :label="def.label"
        :value="textVal(def)"
        :disabled="disabled"
        :prepend-icon="def.icon"
        outlined
        dense
        hide-details
        clearable
        @change="v => setText(def, v)"
      />
      <!-- Valinta -->
      <v-select
        v-else-if="def.valueType === 'SELECT'"
        :items="def.options"
        :label="def.label"
        :value="textVal(def)"
        :disabled="disabled"
        :prepend-icon="def.icon"
        item-text="label"
        item-value="value"
        outlined
        dense
        hide-details
        clearable
        @change="v => setText(def, v)"
      />
      <!-- Totuusarvo -->
      <v-switch
        v-else-if="def.valueType === 'BOOLEAN'"
        :label="def.label"
        :input-value="boolVal(def)"
        :disabled="disabled"
        :prepend-icon="def.icon"
        hide-details
        class="mt-0"
        @change="v => setBool(def, v)"
      />
      <!-- Päivämäärä -->
      <v-text-field
        v-else-if="def.valueType === 'DATE'"
        :label="def.label"
        :value="dateVal(def)"
        :disabled="disabled"
        :prepend-icon="def.icon"
        type="date"
        outlined
        dense
        hide-details
        clearable
        @change="v => setDate(def, v)"
      />
      <!-- Nimikeviittaus -->
      <item-combobox
        v-else-if="def.valueType === 'ITEMREF'"
        :types="def.itemTypeIds"
        :label="def.label"
        :value="itemVal(def)"
        :disabled="disabled"
        hide-details
        @change="v => setItem(def, v)"
      >
        <template v-if="def.icon" v-slot:prepend>
          <v-icon>{{ def.icon }}</v-icon>
        </template>
      </item-combobox>
    </div>
  </div>
</template>

<script>
import ItemCombobox from '@/components/ItemCombobox';

/**
 * Geneerinen attribuuttilomake: piirtää työmenetelmän attribuuttimäärittelyt (defs) tyypin
 * mukaan ja kaksisuuntaisesti sitoo WorkAttributeValueDTO-listaan (v-model). Uusi attribuutti
 * tulee näkyviin automaattisesti pelkän määrittelyn perusteella.
 */
export default {
  name: 'AttributeForm',
  components: { ItemCombobox },
  props: {
    // WorkAttributeDefDTO[] (yhden scopen kentät, esim. WORK tai DAY)
    defs: { type: Array, default: () => [] },
    // WorkAttributeValueDTO[]
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // defId → aiempien päivien summa; null = ei kertymänäyttöä (esim. WORK-scope)
    cumulativeBases: { type: Object, default: null },
  },
  data() {
    return {
      // Kumulatiivisen attribuutin luonnos, defId → { source: 'day'|'total', text } tai
      // { error: 'below'|'empty' }. Luonnos tallentaa sen, mitä käyttäjä kirjoitti; toinen
      // kenttä johdetaan siitä. Kirjoitettavan kentän oma arvo ei siis koskaan muutu sen alta,
      // ja hylkäys on pelkkä luonnoksen vaihto virheeksi, jolloin Yhteensä palautuu
      // sitoutuneeseen arvoon. Virhe näkyy kunnes kumpaan tahansa kenttään kirjoitetaan.
      drafts: {},
    };
  },
  computed: {
    sortedDefs() {
      return [...this.defs].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    },
  },
  watch: {
    // Attribuutit vaihtuivat (toinen työmenetelmä) → myös virheet pois, ne koskivat eri kenttiä
    defs: { immediate: true, handler() { this.drafts = {}; } },
    // Sitoutunut arvolista vaihtui (commit tai toinen merkintä) → luonnokset pois käytöstä
    value() { this.clearDrafts(); },
  },
  methods: {
    valueFor(def) {
      return (this.value || []).find(v => v.defId === def.id);
    },
    numberVal(def) {
      const v = this.valueFor(def);
      return v ? v.valueNumber : null;
    },
    textVal(def) {
      const v = this.valueFor(def);
      return v ? v.valueText : null;
    },
    boolVal(def) {
      const v = this.valueFor(def);
      return v ? v.valueBoolean : false;
    },
    dateVal(def) {
      const v = this.valueFor(def);
      return v ? v.valueDate : null;
    },
    itemVal(def) {
      const v = this.valueFor(def);
      return v && v.valueItem ? v.valueItem : null;
    },
    withUnit(label, unit) {
      return unit ? `${label} (${unit})` : label;
    },
    labelWithUnit(def) {
      return this.withUnit(def.label, def.unit);
    },
    labelTotal(def) {
      return this.withUnit(this.$t('daybook.attribute_total'), def.unit);
    },
    isCumulative(def) {
      return !!def.cumulative && this.cumulativeBases !== null;
    },
    // Saman työn aiempien päivien summa
    baseFor(def) {
      return (this.cumulativeBases && this.cumulativeBases[def.id]) || 0;
    },
    // Kanta on decimal(15,4)
    round(n) {
      return Math.round(n * 10000) / 10000;
    },
    toNumber(v) {
      return v === '' || v == null ? null : Number(v);
    },
    // Päiväarvo → yhteensä. Ilman päiväarvoa tyhjä; kertymä näytetään silloin placeholderina.
    totalOf(def, day) {
      return day == null ? null : this.round(this.baseFor(def) + day);
    },
    // Yhteensä → päiväarvo
    dayOf(def, total) {
      return this.round(total - this.baseFor(def));
    },
    // Luonnokset pois, mutta hylkäysvirhe jää: sitoutunut arvo voi vaihtua toisen kentän
    // tallennuksesta, eikä käyttäjän korjaamaton Yhteensä saa kadota silloin näkyvistä.
    clearDrafts() {
      const kept = {};
      Object.keys(this.drafts).forEach(id => {
        if (this.drafts[id].error === 'below') kept[id] = this.drafts[id];
      });
      this.drafts = kept;
    },
    dayDisplay(def) {
      const draft = this.drafts[def.id] || {};
      if (draft.source === 'day') return draft.text;
      if (draft.source === 'total') {
        const total = this.toNumber(draft.text);
        const day = total == null ? null : this.dayOf(def, total);
        // Kesken syötön (esim. "25.") tai alle kertymän → sitoutunut arvo
        if (day != null && day >= 0) return day;
      }
      return this.numberVal(def);
    },
    totalDisplay(def) {
      const draft = this.drafts[def.id] || {};
      if (draft.source === 'total') return draft.text;
      if (draft.source === 'day') return this.totalOf(def, this.toNumber(draft.text));
      return this.totalOf(def, this.numberVal(def));
    },
    totalPlaceholder(def) {
      const base = this.baseFor(def);
      return base ? String(this.round(base)) : null;
    },
    totalError(def) {
      const draft = this.drafts[def.id] || {};
      if (draft.error === 'below') {
        const base = this.round(this.baseFor(def));
        return [this.$t('daybook.attribute_total_below_base', { value: def.unit ? `${base} ${def.unit}` : base })];
      }
      if (draft.error === 'empty') {
        return [this.$t('daybook.attribute_total_reverted', { label: def.label })];
      }
      return [];
    },
    // Tallennuksen esto: kertymää pienempi Yhteensä jäisi muuten sitoutumatta hiljaisesti.
    // Vain 'below' estää, koska 'empty' on pelkkä palautus eikä hukkaa päiväarvoa.
    hasErrors() {
      return this.sortedDefs.some(def => (this.drafts[def.id] || {}).error === 'below');
    },
    onDayInput(def, v) {
      this.$set(this.drafts, def.id, { source: 'day', text: v });
    },
    onTotalInput(def, v) {
      this.$set(this.drafts, def.id, { source: 'total', text: v });
    },
    // Yhteensä-kentästä poistuttaessa: päiväarvo = yhteensä − kertymä. Tyhjää tai kertymää
    // pienempää ei sitouduta, vaan kenttä palautetaan sitoutuneeseen yhteismäärään ja
    // syy näytetään (tyhjä yhteensä ei saa pyyhkiä päiväarvoa; sen voi tyhjentää päiväkentästä).
    setTotal(def, v) {
      const n = this.toNumber(v);
      const day = n == null ? null : this.dayOf(def, n);
      if (day == null && this.numberVal(def) == null) {
        // Tyhjä pysyy tyhjänä, ei mitään palautettavaa
        this.$delete(this.drafts, def.id);
        return;
      }
      if (day == null || day < 0) {
        this.$set(this.drafts, def.id, { error: day == null ? 'empty' : 'below' });
        return;
      }
      if (day === this.numberVal(def)) {
        // Ei muutosta (esim. Enterillä hylätyn arvon jälkeinen blur, tai "30.0" → 30):
        // virhe jää näkyviin, muu luonnos siivotaan, jotta kenttä näyttää normalisoidun arvon.
        if (!(this.drafts[def.id] || {}).error) this.$delete(this.drafts, def.id);
        return;
      }
      this.setNumber(def, day);
    },
    upsertValue(def, patch) {
      const values = [...(this.value || [])];
      const idx = values.findIndex(v => v.defId === def.id);
      if (idx > -1) {
        values.splice(idx, 1, { ...values[idx], ...patch });
      } else {
        values.push({
          defId: def.id,
          valueNumber: null,
          valueText: null,
          valueBoolean: null,
          valueDate: null,
          valueItem: null,
          ...patch,
        });
      }
      this.$emit('input', values);
    },
    setNumber(def, v) {
      this.upsertValue(def, { valueNumber: this.toNumber(v) });
    },
    setText(def, v) {
      this.upsertValue(def, { valueText: v || null });
    },
    setBool(def, v) {
      this.upsertValue(def, { valueBoolean: !!v });
    },
    setDate(def, v) {
      this.upsertValue(def, { valueDate: v || null });
    },
    setItem(def, item) {
      this.upsertValue(def, { valueItem: item ? { id: item.id, name: item.name } : null });
    },
  },
};
</script>

<style scoped></style>
